import type { ElkEdgeSection } from 'elkjs/lib/elk-api'

/**
 * Anchor point (CENTER, in flow coords) for an edge's on-edge (+) add button.
 *
 * One shape-aware helper shared by custom-edge.tsx and branch-edge.tsx. The anchor
 * is read from the ELK `data.elkSections` (which also draw the SVG path), so the
 * button always sits ON the rendered path; for sibling lanes that share a source
 * row and a convergence row this lands every button at the same Y → centered on
 * the drop AND horizontally aligned across lanes.
 *
 * Shapes (bend count of the first section):
 *  - none           → native midpoint (linear chains routed via getSmoothStepPath).
 *  - >= 4 bends      → empty-lane routing (split → empty variant lane → join): the
 *                      edge jogs out to the lane center between two bends; anchor on
 *                      that lane column.
 *  - >= 2 bends      → L-shape / convergence: midpoint of the FIRST vertical segment
 *                      (source → first bend), which is the per-lane drop.
 *  - straight        → true segment midpoint.
 */
export type EdgeAddButtonAnchorInput = {
    sections: ElkEdgeSection[] | undefined
    sourceX: number
    sourceY: number
    targetX: number
    targetY: number
}

export function getEdgeAddButtonAnchor({
    sections,
    sourceX,
    sourceY,
    targetX,
    targetY,
}: EdgeAddButtonAnchorInput): { addButtonX: number; addButtonY: number } {
    const section = sections?.[0]

    // No ELK routing (linear chains drawn with getSmoothStepPath): native midpoint.
    if (!section) {
        return { addButtonX: sourceX, addButtonY: (sourceY + targetY) / 2 }
    }

    const bends = section.bendPoints ?? []

    // Empty-lane routing: the edge runs out to the lane center, so the source column
    // is not on the long vertical drop — anchor on the lane column between the bends.
    if (bends.length >= 4) {
        return { addButtonX: bends[1].x, addButtonY: (bends[1].y + bends[2].y) / 2 }
    }

    // L-shape / convergence: midpoint of the first vertical segment (source → first
    // bend). Converging siblings share the source row and the convergence row, so
    // this is centered on the drop AND aligned across lanes.
    if (bends.length >= 2) {
        return { addButtonX: section.startPoint.x, addButtonY: (section.startPoint.y + bends[0].y) / 2 }
    }

    // Straight vertical: true segment midpoint.
    return { addButtonX: section.endPoint.x, addButtonY: (section.startPoint.y + section.endPoint.y) / 2 }
}
